/*
 eslint-disable @typescript-eslint/naming-convention
 */
/** What the build itself fixes, injected by Vite: the same in every container running the image. */
declare const APP_BUILD: {
  IS_DEBUG: boolean;
  PUBLIC_PATH: string | null;
  GIT_HASH: string;
};

/**
 * The runtime configuration, set by /config.js before the app loads. The container writes it when
 * it starts from its environment, which the Vault Secrets Operator fills from Vault; the dev
 * server and the test builds write it from their env files (build/runtime-config.sh).
 */
interface RuntimeConsts {
  REGION: 'EU' | 'US';
  BACKEND_ADDRESS: string;
  CAP_GRID_TO_IMAGES?: boolean;
  ROUTER_BASE_URL?: string;
  /** The other region's Balm, which the sign-up page links to for a reader on the wrong domain. */
  OTHER_REGION_URL?: string;
  MEDIA_URL: string;
  RECCO_MEDIA_URL?: string;
  LANGUAGE: 'de' | 'en';
  MULTI_LANGUAGE: boolean;
  SHOW_WITHDRAW_BTN: boolean;
  SHOW_UNLOCK_BTN: boolean;
  LOGOUT_ON_401: boolean;
  DISABLE_LOADING?: boolean;
  PRIVATE_CAPTCHA_SITE_KEY?: string;
  PRIVATE_CAPTCHA_RESEND_SITE_KEY?: string;
  SHOW_REVISION?: boolean;
  SHOW_SCREEN_SIZE?: boolean;
  SHOW_PAYMENT_FEATURES?: boolean,
  LOG_LEVEL: string;
  POSTHOG?: {
    API_KEY: string;
    HOST: string;
  },
  SENTRY?: {
    DSN: string;
    ENVIRONMENT: string;
    TRACES_SAMPLE_RATE?: number;
  },
  SIGN_UP_FORM_DEFAULT_VALUES?: {
    email: string;
    name: string;
    birthDate: string;
    marketingCode: string;
  }
  ZITADEL: ZitadelConfig;
}

declare global {
  interface Window {
    APP_CONSTS?: RuntimeConsts;
  }
}

if (!window.APP_CONSTS) {
  throw new Error('window.APP_CONSTS is not set: /config.js did not load before the app');
}

const APP_CONSTS: RuntimeConsts & typeof APP_BUILD = {...window.APP_CONSTS, ...APP_BUILD};

/** The application constants: the runtime configuration and what the build fixes */
export const {
  IS_DEBUG,
  REGION,
  BACKEND_ADDRESS,
  DISABLE_LOADING,
  PUBLIC_PATH,
  GIT_HASH,
  ROUTER_BASE_URL,
  OTHER_REGION_URL,
  ZITADEL,
  SIGN_UP_FORM_DEFAULT_VALUES,
  SHOW_WITHDRAW_BTN,
  SHOW_UNLOCK_BTN,
  MEDIA_URL,
  RECCO_MEDIA_URL,
  LANGUAGE,
  LOG_LEVEL,
  MULTI_LANGUAGE,
  LOGOUT_ON_401,
  SHOW_REVISION,
  SHOW_SCREEN_SIZE,
  SHOW_PAYMENT_FEATURES,
  POSTHOG,
  SENTRY,
  PRIVATE_CAPTCHA_SITE_KEY,
  PRIVATE_CAPTCHA_RESEND_SITE_KEY,
} = APP_CONSTS;

/** Whether to cap CSS grid layouts to image dimensions (defaults to false in dev, true elsewhere) */
export const CAP_GRID_TO_IMAGES = APP_CONSTS.CAP_GRID_TO_IMAGES ?? !IS_DEBUG;

/** Zitadel OIDC identity provider configuration */
export interface ZitadelConfig {
  organization_resource_id: string;
  project_resource_id: string;
  issuer: string;
  client_id: string;
  /**
   * Google identity provider, as configured in this environment's Zitadel org (IPD-2844). Sending
   * it as a scope on the authorize request redirects straight to Google instead of to Zitadel's
   * sign-in screen. Absent where the provider is not configured yet, which keeps the hosted screen.
   */
  google_idp_id?: string;
}

/** Regex patterns for extracting, detecting, and removing language prefixes from URL paths */
export const LANGUAGE_REGEX = {
  // Matches language prefix in URL paths: /en/, /de/, /en, /de
  PATH_WITH_LANG: /^\/(?<lang>[a-z]{2})(?:\/|$)/u,

  // Matches paths that start with language code: /en/something, /de/something
  STARTS_WITH_LANG: /^\/[a-z]{2}(?:\/|$)/u,

  // Removes language prefix from paths: /en/explore -> /explore
  REMOVE_LANG_PREFIX: /^\/[a-z]{2}/u,
} as const;

